import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { scrollToId, scrollToBrief } from "@/lib/scroll";

const LINKS = [
  { label: "Proof", id: "proof" },
  { label: "How it works", id: "how-it-works" },
  { label: "Pricing", id: "pricing" },
  { label: "FAQ", id: "faq" },
];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        scrolled ? "border-b border-zinc-900 bg-black/80 backdrop-blur-md" : "bg-transparent"
      }`}
      data-testid="nav-bar"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 sm:px-8">
        <button
          onClick={() => scrollToId("top", 0)}
          data-testid="nav-logo"
          className="font-display text-xl font-800 tracking-tight text-white"
        >
          FLOWNAVY<span className="text-zinc-500">.</span>
        </button>

        <nav className="hidden items-center gap-9 md:flex">
          {LINKS.map((l) => (
            <button
              key={l.id}
              onClick={() => go(l.id)}
              data-testid={`nav-link-${l.id}`}
              className="ff-mono text-xs uppercase tracking-widest text-zinc-400 transition-colors hover:text-white"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={scrollToBrief}
            data-testid="nav-cta"
            className="hidden rounded-full bg-[#C7FF3C] px-5 py-2.5 text-xs font-semibold text-black transition-colors hover:bg-[#b4ec2a] sm:inline-flex"
          >
            Claim free sample
          </button>
          <button
            className="md:hidden text-white"
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            aria-label="Menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden border-t border-zinc-900 bg-black/95 backdrop-blur-md md:hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="flex flex-col gap-1 px-5 py-4">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  onClick={() => go(l.id)}
                  className="py-2 text-left ff-mono text-sm uppercase tracking-widest text-zinc-300"
                >
                  {l.label}
                </button>
              ))}
              <button
                onClick={() => go("brief-form")}
                className="mt-2 rounded-full bg-[#C7FF3C] px-5 py-3 text-sm font-semibold text-black"
              >
                Claim free sample
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
